import L from 'leaflet'
import * as topojson from 'topojson-client'
import worldAtlas from 'world-atlas/countries-50m.json'

import markerIconUrl from 'leaflet/dist/images/marker-icon.png'
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png'

import { HIGHLIGHT_STYLE, curveTypesFor, isCovered, isJapanVisible, powerZoneFor, resolveTabSelection, styleFor, zoneMembers, type CountryStyle, type MapView, type PowerZone, type Tab } from './coverage'
import {
    JAPAN_CURVE_TYPE_LABEL,
    europeThumbnailSvg,
    japanPrefectures,
    japanThumbnailSvg,
    regionInfo,
} from './japan'
import { ZONE_SPLIT_COUNTRIES, biddingZoneInfo, powerBiddingZones, type BiddingZone } from './bidding-zones'
import { COUNTRY_META, type CountryMeta } from './country-meta'
import { readRegionFill, restoreRegionFill } from './map-hatch'

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({ iconUrl: markerIconUrl, shadowUrl: markerShadowUrl })

const ISO_TO_NAME: Record<string, string> = {
    '578': 'Norway', '752': 'Sweden', '246': 'Finland', '208': 'Denmark',
    '826': 'United Kingdom', '372': 'Ireland', '620': 'Portugal', '724': 'Spain',
    '528': 'Netherlands', '056': 'Belgium', '442': 'Luxembourg',
    '276': 'Germany', '250': 'France', '756': 'Switzerland', '040': 'Austria',
    '380': 'Italy', '616': 'Poland', '203': 'Czech Republic', '703': 'Slovakia',
    '348': 'Hungary', '642': 'Romania', '100': 'Bulgaria', '688': 'Serbia',
    '191': 'Croatia', '705': 'Slovenia', '300': 'Greece',
}

interface CountryLayer {
    name: string
    layer: L.GeoJSON
}

const FLAME_ICON = '\u{1F525}'

/** Adapt a pure CountryStyle into a Leaflet PathOptions object. */
function toPathOptions(style: CountryStyle): L.PathOptions {
    return {
        color: style.color,
        fillColor: style.fillColor,
        fillOpacity: style.fillOpacity,
        weight: style.weight,
        opacity: 0.9,
    }
}

/** Build the rich hover tooltip for a covered country on the active tab. */
function buildTooltipHtml(name: string, meta: CountryMeta | undefined, tab: Tab): string {
    const feed = tab === 'power' ? meta?.power : meta?.gas
    const icon = tab === 'power' ? '⚡' : FLAME_ICON
    const label = tab === 'power' ? 'Power:' : 'Gas:'

    let feedsHtml = ''
    if (feed) {
        feedsHtml += '<div class="ktt-feed"><span class="ktt-icon">' + icon + '</span><span class="ktt-label">' + label + '</span><span class="ktt-val">' + feed + '</span></div>'
    }

    const prodsHtml = curveTypesFor(meta ?? {}, tab).join(' · ')

    return '<div class="ktt">'
        + '<div class="ktt-header"><span class="ktt-flag">' + (meta?.flag || '') + '</span><span class="ktt-name">' + name + '</span></div>'
        + '<div class="ktt-divider"></div>'
        + '<div class="ktt-section-label">Covered feeds</div>'
        + feedsHtml
        + (prodsHtml ? '<div class="ktt-divider"></div><div class="ktt-products">' + prodsHtml + '</div>' : '')
        + '</div>'
}

/**
 * Build the hover tooltip for a power bidding zone (power tab only). The zone
 * still highlights every member together on the map, but the tooltip names only
 * the zone's feed country (Germany for DE-LU) — its flag, its power feed and its
 * curve-type line. The other members (Luxembourg) are deliberately not mentioned
 * by name or flag, so the tooltip reads as a plain Germany entry (#315).
 */
function buildZoneTooltipHtml(zone: PowerZone): string {
    const feedMeta = COUNTRY_META[zone.feedCountry]
    const feed = feedMeta?.power
    const feedFlag = feedMeta?.flag || ''

    let feedsHtml = ''
    if (feed) {
        feedsHtml += '<div class="ktt-feed"><span class="ktt-icon">⚡</span><span class="ktt-label">Power:</span><span class="ktt-val">' + feed + '</span></div>'
    }

    // Zone tooltips are power-only, so the full curve-type set (incl. fundamental) applies.
    const prodsHtml = curveTypesFor(feedMeta ?? {}, 'power').join(' · ')

    return '<div class="ktt">'
        + '<div class="ktt-header"><span class="ktt-flag">' + feedFlag + '</span><span class="ktt-name">' + zone.feedCountry + '</span></div>'
        + '<div class="ktt-divider"></div>'
        + '<div class="ktt-section-label">Covered feeds</div>'
        + feedsHtml
        + (prodsHtml ? '<div class="ktt-divider"></div><div class="ktt-products">' + prodsHtml + '</div>' : '')
        + '</div>'
}

function createCountryLayer(map: L.Map, feature: GeoJSON.Feature, tab: Tab): CountryLayer | null {
    const id = String(feature.id || '')
    const idPadded = id.length < 3 ? ('000' + id).slice(-3) : id
    const name = ISO_TO_NAME[idPadded] || ISO_TO_NAME[id]
    if (!name) return null

    const style = styleFor(name, tab)
    const layer = L.geoJSON(feature, { style: toPathOptions(style) }).addTo(map)

    return { name, layer }
}

/** Build the hover tooltip for a power bidding zone (NO/SE/IT) — code + label,
 *  the parent country's flag, the zone's KYOS power feed and curve types. */
function buildBiddingZoneTooltipHtml(code: string, info: BiddingZone): string {
    const flag = COUNTRY_META[info.country]?.flag || ''
    return '<div class="ktt">'
        + '<div class="ktt-header"><span class="ktt-flag">' + flag + '</span><span class="ktt-name">' + code + ' — ' + info.name + '</span></div>'
        + '<div class="ktt-divider"></div>'
        + '<div class="ktt-section-label">Covered feeds</div>'
        + '<div class="ktt-feed"><span class="ktt-icon">⚡</span><span class="ktt-label">Power:</span><span class="ktt-val">' + info.feed + '</span></div>'
        + '<div class="ktt-divider"></div><div class="ktt-products">Forward curves · Fundamental curves · Simulation curves</div>'
        + '</div>'
}

/**
 * Build the power bidding-zone overlay (NO/SE/IT) as one geoJSON layer. Shown on
 * the power tab only (added/removed by applyTab). Every zone is covered, so each
 * is styled like a covered power country with its own tooltip + per-zone hover
 * emphasis — hovering one zone lights that zone alone, never the whole country.
 */
function createZoneLayer(): L.GeoJSON {
    const resting = toPathOptions(HIGHLIGHT_STYLE.power)
    return L.geoJSON(powerBiddingZones as unknown as GeoJSON.FeatureCollection, {
        style: () => resting,
        onEachFeature: (feature, layer) => {
            const code = feature?.properties?.zone as string | undefined
            const info = code ? biddingZoneInfo(code) : undefined
            if (!info) return

            layer.bindTooltip(buildBiddingZoneTooltipHtml(code as string, info), {
                sticky: true,
                direction: 'bottom',
                offset: L.point(0, 8),
                className: 'kyos-map-tooltip ktt-outer',
                opacity: 1,
            })
            layer.on('mouseover', function (this: L.Path) { this.setStyle({ fillOpacity: 0.85, weight: 2 }) })
            layer.on('mouseout', function (this: L.Path) { this.setStyle(resting) })
        },
    })
}

/** Re-style every layer for the active tab and refresh its tooltip + hover. */
function applyTab(map: L.Map, layers: CountryLayer[], zoneLayer: L.GeoJSON, tab: Tab): void {
    // name → layer lookup so a hover on one country can re-style every other
    // member of its power zone (e.g. hovering Germany also lights Luxembourg).
    const byName = new Map(layers.map(({ name, layer }) => [name, layer]))

    // Power bidding zones (NO/SE/IT) overlay the power map only.
    if (tab === 'power') {
        if (!map.hasLayer(zoneLayer)) zoneLayer.addTo(map)
    } else if (map.hasLayer(zoneLayer)) {
        map.removeLayer(zoneLayer)
    }

    layers.forEach(({ name, layer }) => {
        layer.off('mouseover')
        layer.off('mouseout')
        layer.unbindTooltip()

        // On the power tab IT/SE/NO are drawn as bidding zones (zoneLayer)
        // instead of one country shape — hide their country polygon so the two
        // aren't double-rendered. They behave normally on the gas tab.
        if (tab === 'power' && ZONE_SPLIT_COUNTRIES.has(name)) {
            if (map.hasLayer(layer)) map.removeLayer(layer)
            return
        }
        // Power and Gas are autonomous maps. A country covered for the *other*
        // commodity (e.g. a power-only country on the Gas tab) is not part of
        // THIS map, so don't paint it as a distinct grey shape — remove its
        // polygon and let the base map show through, exactly like any country
        // KYOS never covers. Greying it read as a broken "intersection" zone.
        if (!isCovered(name, tab)) {
            if (map.hasLayer(layer)) map.removeLayer(layer)
            return
        }
        if (!map.hasLayer(layer)) layer.addTo(map)

        const style = styleFor(name, tab)
        layer.setStyle(toPathOptions(style))

        // On the power tab a zone member emphasises every member together on
        // hover (e.g. hovering Luxembourg also lights Germany), but the tooltip
        // names only the zone's feed country; otherwise it behaves per-country.
        const zone = tab === 'power' ? powerZoneFor(name) : null
        const members = zoneMembers(name, tab)

        const tooltipHtml = zone ? buildZoneTooltipHtml(zone) : buildTooltipHtml(name, COUNTRY_META[name], tab)
        // Pin the tooltip below the cursor with a fixed direction. Without an
        // explicit direction Leaflet uses 'auto', which flips the wide tooltip
        // from one side of the cursor to the other based on the cursor's
        // position relative to the map centre — so hovering the centre meridian
        // (15°E ≈ the Germany/Poland border) flipped it every frame and it read
        // as flicker (#302). A fixed direction stops the flip. 'bottom' is used
        // because the covered countries cluster in the map's upper half, so the
        // tooltip renders down into the map body rather than up toward the edge.
        layer.bindTooltip(tooltipHtml, {
            sticky: true,
            direction: 'bottom',
            offset: L.point(0, 8),
            className: 'kyos-map-tooltip ktt-outer',
            opacity: 1,
        })

        layer.on('mouseover', function () {
            members.forEach((member) => byName.get(member)?.setStyle({ fillOpacity: 0.85, weight: 2 }))
        })
        layer.on('mouseout', function () {
            members.forEach((member) => byName.get(member)?.setStyle(toPathOptions(styleFor(member, tab))))
        })
    })
}

// Wire the commodity tabs AND the EU↔JP view switch together. They share state
// because one tab click can change the view: resolveTabSelection encodes the
// rule that clicking Gas on Japan (which is power-only) navigates back to the
// Europe map with Gas active, while entering Japan via the inset always lands
// on Power.
function wireControls(map: L.Map, layers: CountryLayer[], zoneLayer: L.GeoJSON): void {
    const buttons = Array.from(document.querySelectorAll<HTMLElement>('#kyos-map-tabs [data-tab]'))
    const legendRows = Array.from(document.querySelectorAll<HTMLElement>('#kyos-map-legend [data-tab-legend]'))
    // The persistent "Via partner MPX (Japan)" legend row. Power-only like the
    // rest of Japan, so it follows the inset and hides on the gas tab.
    const mpxLegend = document.querySelector<HTMLElement>('#kyos-map-legend [data-map-legend="mpx"]')

    const stage = document.querySelector<HTMLElement>('.map-stage')
    const overlay = document.getElementById('japan-overlay')
    const inset = document.getElementById('map-inset')

    // Apply a commodity tab to the Europe map UI (active button, legend rows,
    // country restyle, Leaflet size invalidation).
    function setTab(tab: Tab): void {
        buttons.forEach((btn) => btn.classList.toggle('active', btn.dataset.tab === tab))
        legendRows.forEach((row) => { row.hidden = row.dataset.tabLegend !== tab })
        // Japan is power-only: hide its map switcher + MPX legend row on gas so
        // no Japan reference survives on the gas map (the inset's [hidden] wins
        // over its `display:flex` via Tailwind's `display:none !important`).
        const japanShown = isJapanVisible(tab)
        if (inset) inset.hidden = !japanShown
        if (mpxLegend) mpxLegend.hidden = !japanShown
        applyTab(map, layers, zoneLayer, tab)
        // Re-styling can run while the section is offscreen; invalidating the
        // size forces Leaflet to recompute and re-request tiles so none stay grey.
        map.invalidateSize()
    }

    let view: MapView = 'europe'
    let japanMap: L.Map | null = null

    function setView(next: MapView): void {
        if (!stage || !overlay || !inset) return
        view = next
        stage.setAttribute('data-view', view)
        overlay.setAttribute('aria-hidden', String(view !== 'japan'))

        // The inset previews the OTHER view (faded silhouette via CSS). The
        // thumbnails are already framed, so we only strip width/height.
        inset.innerHTML = view === 'europe' ? japanThumbnailSvg : europeThumbnailSvg
        const insetSvg = inset.querySelector<SVGSVGElement>('svg')
        if (insetSvg) {
            insetSvg.removeAttribute('width')
            insetSvg.removeAttribute('height')
        }
        inset.setAttribute(
            'aria-label',
            view === 'europe' ? 'Switch coverage map to Japan' : 'Switch coverage map to Europe',
        )

        if (view === 'japan') {
            // Lazy-init once; the container has size even at opacity 0, but
            // invalidateSize() after it is shown guards against grey tiles.
            if (!japanMap) japanMap = createJapanMap()
            japanMap.invalidateSize()
            // Japan is power-only: always land on the Power tab on arrival.
            setTab('power')
        } else {
            // Returning to Europe — the map may have been inert/offscreen, so
            // make sure Leaflet recomputes (mirrors the tab logic).
            map.invalidateSize()
        }
    }

    // A tab click may also switch the view (Gas on Japan → Europe + Gas); every
    // other click keeps the current view and just activates the clicked tab.
    buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
            const clicked = btn.dataset.tab as Tab | undefined
            if (!clicked) return
            const resolved = resolveTabSelection(view, clicked)
            if (resolved.view !== view) setView(resolved.view)
            setTab(resolved.tab)
        })
    })

    if (stage && overlay && inset) {
        // The "Fundamental curves only" cue, distinguishing Japan from Europe's
        // 3 curve types. Sits above the Japan tiles via z-index (see CSS).
        const caption = document.createElement('div')
        caption.className = 'japan-overlay-caption'
        caption.textContent = 'Japan · Fundamental curves only'
        overlay.appendChild(caption)

        // Clicking the inset (which previews the inactive view) flips the view.
        inset.addEventListener('click', () => setView(view === 'europe' ? 'japan' : 'europe'))

        setView('europe')
    }

    setTab('power')
}

// ===========================================================================
// EU↔JP view-state machine. ORTHOGONAL to the power/gas tab state: the Japan
// inset/overlay shows on both tabs. State lives as a [data-view] attribute on
// the .map-stage element; CSS drives the ~200ms opacity cross-fade between the
// Europe Leaflet map (#kyos-map) and the Japan overlay (#japan-overlay), which
// hosts a SECOND Leaflet map (#japan-map). Clicking the corner inset
// (#map-inset) flips between the two views; the Japan map is lazy-initialised
// the first time the user switches to it (a Leaflet map needs a sized, visible
// container, and the overlay is position:absolute; inset:0 so it always has
// size — but we still invalidateSize() once it is faded in to be safe).
// ===========================================================================

/** Solid power-blue fallback fill for covered Japan regions (used if the hatch pattern can't be applied). */
const JAPAN_COVERED_FILL = '#3b82f6'
const JAPAN_COVERED_FILL_OPACITY = 0.45
const JAPAN_COVERED_STROKE = '#1d4ed8'

/**
 * Power-blue diagonal hatch for the covered Japan regions (mirrors the "Via MPX
 * Partners" legend swatch). The gaps are transparent so the CartoDB basemap
 * shows through, giving the regions a rich, layered look.
 */
const MPX_HATCH_DEFS =
    '<pattern id="mpx-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">'
    + '<rect width="6" height="6" fill="#3b82f6" fill-opacity="0.12"></rect>'
    + '<line x1="0" y1="0" x2="0" y2="6" stroke="#1d4ed8" stroke-width="1.4"></line>'
    + '</pattern>'

/** Leaflet PathOptions for a covered Japan region (solid-blue fallback before the hatch is injected). */
function coveredRegionStyle(): L.PathOptions {
    return {
        color: JAPAN_COVERED_STROKE,
        fillColor: JAPAN_COVERED_FILL,
        fillOpacity: JAPAN_COVERED_FILL_OPACITY,
        weight: 1,
        opacity: 0.9,
    }
}

/** Leaflet PathOptions for the uncovered Okinawa region (muted grey). */
function uncoveredRegionStyle(): L.PathOptions {
    return {
        color: '#94a3b8',
        fillColor: '#cbd5e1',
        fillOpacity: 0.4,
        weight: 0.5,
        opacity: 0.9,
    }
}

/** Build the rich hover tooltip for a covered Japan region (mirrors the country tooltip's header + products). */
function buildJapanTooltipHtml(name: string): string {
    return '<div class="ktt">'
        + '<div class="ktt-header"><span class="ktt-name">' + name + '</span></div>'
        + '<div class="ktt-divider"></div>'
        + '<div class="ktt-products">' + JAPAN_CURVE_TYPE_LABEL + '</div>'
        + '</div>'
}

/**
 * Inject the #mpx-hatch <pattern> into the Japan map's overlay-pane <svg> <defs>
 * (Leaflet has no fill-pattern API), then point each covered region's path fill
 * at url(#mpx-hatch). Guarded against running twice and wrapped in try/catch so
 * a hatch failure never breaks the map — covered regions keep their solid-blue
 * fallback fill in that case.
 */
function applyMpxHatch(japanMap: L.Map, regionLayer: L.GeoJSON): void {
    try {
        const svg = japanMap.getPane('overlayPane')?.querySelector('svg')
        if (!svg) return

        // Insert <defs> with the pattern as the svg's first child (once only).
        if (!svg.querySelector('#mpx-hatch')) {
            const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
            defs.innerHTML = MPX_HATCH_DEFS
            svg.insertBefore(defs, svg.firstChild)
        }

        regionLayer.eachLayer((layer) => {
            const region = regionInfo((layer as L.GeoJSON).feature?.properties?.region)
            if (!region?.covered) return
            const el = (layer as L.Polygon).getElement()
            if (el) el.setAttribute('fill', 'url(#mpx-hatch)')
        })
    } catch {
        // Leave the solid-blue fallback fill on covered regions.
    }
}

// CARTO's raster basemaps now require an API key (the keyless PNG tiles are
// being retired). The key is domain-restricted, so it's safe to ship in the
// bundle — the domain allowlist, not secrecy of this string, is what guards it.
const CARTO_TILE_URL =
    'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png' +
    '?key=cb1_2drf_1_3ef0902350a3c5c39b634f1c'

/** Add the shared CARTO light basemap layer to a map. */
function addCartoBasemap(map: L.Map): void {
    L.tileLayer(CARTO_TILE_URL, {
        attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
    }).addTo(map)
}

/**
 * Build the second Leaflet map for Japan inside #japan-map: same CartoDB tiles
 * as Europe, with the bundled prefecture polygons drawn as one geoJSON layer
 * styled per JEPX/MPX region (covered = blue hatch, Okinawa = muted grey) with
 * per-region tooltips + hover.
 */
function createJapanMap(): L.Map {
    const japanMap = L.map('japan-map', {
        center: [37.6, 137.8],
        zoom: 5,
        zoomControl: true,
        scrollWheelZoom: false,
        attributionControl: true,
        minZoom: 4,
        maxZoom: 7,
    })

    addCartoBasemap(japanMap)

    const regionLayer = L.geoJSON(japanPrefectures as unknown as GeoJSON.FeatureCollection, {
        style: (feature) => {
            const region = regionInfo(feature?.properties?.region)
            return region?.covered ? coveredRegionStyle() : uncoveredRegionStyle()
        },
        onEachFeature: (feature, layer) => {
            const region = regionInfo(feature?.properties?.region)
            if (!region?.covered) return

            layer.bindTooltip(buildJapanTooltipHtml(region.name), {
                sticky: true,
                className: 'kyos-map-tooltip ktt-outer',
                opacity: 1,
            })
            // Emphasise on hover via opacity + weight only. In Leaflet 1.9.4
            // SVG._updateStyle() re-sets the path's `fill` to the solid fallback
            // on EVERY setStyle (mouseover AND mouseout), clobbering the injected
            // url(#mpx-hatch). So we capture the element's resting fill first,
            // then re-apply the hatch afterwards (restoreRegionFill is a no-op
            // for the solid-fallback case, so that path hovers cleanly too).
            const emphasise = (path: L.Path, fillOpacity: number, weight: number) => {
                const el = (path as L.Polygon).getElement()
                const restingFill = readRegionFill(el)
                path.setStyle({ fillOpacity, weight })
                restoreRegionFill(el, restingFill)
            }
            layer.on('mouseover', function (this: L.Path) { emphasise(this, 0.7, 2) })
            layer.on('mouseout', function (this: L.Path) { emphasise(this, JAPAN_COVERED_FILL_OPACITY, 1) })
        },
    }).addTo(japanMap)

    // Swap the covered regions' solid fill for the hatch pattern once the SVG
    // paths exist in the overlay pane.
    applyMpxHatch(japanMap, regionLayer)

    return japanMap
}

function initMap(): void {
    const mapEl = document.getElementById('kyos-map')
    if (!mapEl) return

    const map = L.map('kyos-map', {
        center: [54, 15],
        zoom: 4,
        zoomControl: true,
        scrollWheelZoom: false,
        attributionControl: true,
        minZoom: 3,
        maxZoom: 7,
    })

    addCartoBasemap(map)

    const geojson = topojson.feature(worldAtlas as any, (worldAtlas as any).objects.countries) as unknown as GeoJSON.FeatureCollection
    const layers: CountryLayer[] = []
    geojson.features.forEach((f) => {
        const countryLayer = createCountryLayer(map, f, 'power')
        if (countryLayer) layers.push(countryLayer)
    })

    const zoneLayer = createZoneLayer()

    wireControls(map, layers, zoneLayer)
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initMap())
} else {
    initMap()
}
